import { debounce } from "lodash";
import { useState } from "react";
import {
    GuxDropdown,
    GuxListbox,
    GuxOption,
} from "genesys-spark-components-react";

export type SearchResult = {
    name: string;
    value: string;
    version: number;
};

export function createSearchResult(
    name = "",
    value = "",
    version = -1
): SearchResult {
    return {
        name: name,
        value: value,
        version: version,
    };
}

const SearchDropdown = function ({
    onSearch,
    onSelection,
    onExpanded,
    onCollapsed,
    results,
    selected,
    disabled,
    placeholder,
    hasError,
    ariaLabel,
}: {
    onSearch: (input: string) => void; // Input will always be a string (including empty string)
    onSelection: (input: SearchResult) => void;
    onExpanded?: (input: string) => void; // Input will always be a string (including empty string)
    onCollapsed?: () => void;
    results: SearchResult[];
    selected?: SearchResult;
    placeholder?: string;
    hasError?: boolean;
    disabled?: boolean;
    ariaLabel?: string;
}) {
    const debouncedSearch = debounce((newTerm) => {
        onSearch(newTerm);
        setSearched(newTerm);
    }, 300);

    const [searched, setSearched] = useState<string>(selected?.name ?? "");
    const options = [
        ...results,
        ...(selected && !results.length ? [selected] : []),
    ];
    return (
        <GuxDropdown
            filterType="custom"
            placeholder={placeholder}
            value={selected?.value}
            hasError={hasError}
            disabled={disabled}
            onGuxfilter={(event) => {
                if (event.detail) {
                    debouncedSearch(event.detail);
                }
            }}
            onInput={(event) => {
                const target = event.target as HTMLInputElement;
                const result = results.find(
                    (result) => result.value === target.value
                );
                const newSelection = createSearchResult(
                    result?.name,
                    target.value,
                    result?.version
                );
                onSelection(newSelection);
            }}
            onGuxcollapsed={() => {
                if (onCollapsed) {
                    onCollapsed();
                }
            }}
            onGuxexpanded={() => {
                if (onExpanded) {
                    onExpanded(searched);
                }
            }}
        >
            <GuxListbox aria-label={ariaLabel}>
                {options.map((op, count) => (
                    <GuxOption value={op.value} key={count}>
                        <span>{op.name}</span>
                    </GuxOption>
                ))}
            </GuxListbox>
        </GuxDropdown>
    );
};

export default SearchDropdown;
